/**
 * @file feature-catalog.js
 * @description Catálogo declarativo de feature flags del producto. El tablero
 *              de Plataforma usa este archivo como fuente de verdad para
 *              renderizar los switches del tab Módulos. Cada feature tiene:
 *
 *                - `key`: identificador único usado en `Organization.features`
 *                  y en `requireFeature(key)` del backend.
 *                - `label`: texto humano que ve el platform-admin.
 *                - `description`: ayuda corta debajo del label.
 *                - `presets`: nombres de los presets donde la feature viene
 *                  encendida por default (sirve para mostrar el badge "default
 *                  off / default on" en el tab Módulos).
 *
 *              SEMÁNTICA DEL DEFAULT: feature ausente en `Organization.features`
 *              ⇒ ENCENDIDA. Esta lista es solo para UI — el backend no
 *              consulta este archivo, lee directo de Mongo.
 *
 *              IMPORTANTE: si agregás una feature acá, asegurate de:
 *                1. Aplicar `requireFeature('clave')` al endpoint(s) que
 *                   querés bloquear cuando la feature esté apagada.
 *                2. Agregar la directiva `v-has-feature="'clave'"` a los
 *                   items de UI que deben ocultarse.
 *                Hasta que ambas cosas pasen, el switch del tablero
 *                "no hace nada visible".
 */

export const FEATURE_GROUPS = [
  {
    id: "clinic",
    title: "Clínico",
    features: [
      {
        key: "clinic.patients",
        label: "Gestión de pacientes",
        description:
          "Listado, alta, edición, dashboard individual del paciente.",
      },
      {
        key: "clinic.history",
        label: "Historia clínica",
        description: "Atenciones previas, evolución, antecedentes, alergias.",
      },
      {
        key: "clinic.appointments",
        label: "Agenda de turnos",
        description: "Calendario, reservas, slots por médico.",
      },
      {
        key: "clinic.attentions",
        label: "Sala de atenciones",
        description: "Atender pacientes en consulta, presencial o virtual.",
      },
      {
        key: "clinic.prescriptions",
        label: "Recetas electrónicas",
        description:
          "Prescripción digital con QR firmado (requiere prescriptor en Integraciones).",
      },
      {
        key: "clinic.orders",
        label: "Órdenes médicas",
        description: "Laboratorio, imagen, prácticas, certificados.",
      },
    ],
  },
  {
    id: "followup",
    title: "Seguimiento de pacientes",
    features: [
      {
        key: "followup.protocols",
        label: "Protocolos y treatments",
        description: "Asignar tratamientos con tareas programadas al paciente.",
      },
      {
        key: "followup.episodes",
        label: "Episodios y tareas",
        description: "Manejo de episodios clínicos con tareas y monitoreo.",
      },
      {
        key: "followup.alerts",
        label: "Dashboard de monitoreo",
        description: "Vista de alertas/aggregates para gestores y médicos.",
      },
      {
        key: "monitoring.healthConversation",
        label: "Conversaciones de salud (IA)",
        description:
          "Nuevo tipo de task: conversación abierta vía WhatsApp conducida por IA con score 1-10 + semáforo verde/amarillo/rojo. Requiere comm.whatsapp encendida.",
      },
    ],
  },
  {
    id: "urgency",
    title: "Urgencias",
    features: [
      {
        key: "urgency",
        label: "Módulo de Urgencias",
        description:
          "Cola de pacientes urgentes + asignación a prestadores. Apagar si la org NO maneja guardias/urgencias (ej. consultorio programado solamente).",
      },
    ],
  },
  {
    id: "ooss",
    title: "Obras sociales — core",
    features: [
      {
        key: "ooss.members",
        label: "Padrón de afiliados",
        description: "Gestión de Members vinculados a la OS.",
      },
      {
        key: "ooss.credential",
        label: "Credencial virtual",
        description:
          "Credencial digital del afiliado para mostrar en consultorio.",
      },
      {
        key: "ooss.token",
        label: "Token de validación",
        description: "OTP del afiliado para presentar en validador.",
      },
      {
        key: "ooss.validator",
        label: "Validador para prestadores",
        description:
          "Validación en consultorio: prestador ingresa AUTH + OTP y obtiene ServiceRecord.",
      },
      {
        key: "ooss.authorizations",
        label: "Autorizaciones (AUTH cases)",
        description: "Solicitud y aprobación de prácticas autorizadas.",
      },
      {
        key: "ooss.audit",
        label: "Auditoría médica",
        description: "Revisión de prácticas, observaciones por auditor.",
      },
      {
        key: "ooss.contracts",
        label: "Contratos y planes",
        description: "Maestros de contratos, planes, nomenclador.",
      },
      {
        key: "ooss.providers",
        label: "Prestadores y red",
        description: "Gestión de prestadores, instituciones de red.",
      },
      {
        key: "ooss.billing",
        label: "Cuenta médica + liquidaciones",
        description: "Cierre de período, liquidación a prestadores.",
      },
    ],
  },
  {
    id: "mobile",
    title: "Mobile",
    features: [
      {
        key: "mobile.patient",
        label: "App del paciente",
        description: "App mobile para el paciente del consultorio.",
      },
      {
        key: "mobile.affiliate",
        label: "App del afiliado OS",
        description:
          "App mobile para el afiliado de la obra social (credencial, token).",
      },
    ],
  },
  {
    id: "comm",
    title: "Comunicación",
    features: [
      {
        key: "comm.email",
        label: "Emails transaccionales",
        description: "Turnos, recordatorios, recetas, reset password.",
      },
      {
        key: "comm.whatsapp",
        label: "WhatsApp (2chat)",
        description: "Notificaciones al paciente vía WhatsApp.",
      },
      {
        key: "comm.push",
        label: "Push móvil (FCM)",
        description: "Solo aplica si alguna app mobile está habilitada.",
      },
    ],
  },
  {
    id: "reports",
    title: "BI y reportes",
    features: [
      {
        key: "reports.metabase",
        label: "Reportes (Metabase)",
        description:
          "Habilita la sección 'Reportes' en el sidebar del Dashboard. Cada dashboard se arma en Metabase y se embed firmando un JWT por tenant/user (ver components/embedding del API).",
      },
    ],
  },
  {
    id: "integ",
    title: "Integraciones",
    features: [
      {
        key: "integ.prescriptor",
        label: "Prescriptor externo de recetas",
        description: "Servicio externo que firma recetas con QR.",
      },
      {
        key: "integ.webhookAppointments",
        label: "Webhook entrante de turnos",
        description:
          "Recibir turnos desde un sistema externo (creación idempotente de pacientes).",
      },
      {
        key: "integ.pacs",
        label: "PACS (imagenología)",
        description: "Integración con PACS Deragopyan u otro proveedor.",
      },
    ],
  },
];

/**
 * @description Devuelve TODAS las features atómicas como array plano (sin
 *              agrupar). Útil para validar que todas las claves usadas en
 *              `requireFeature` están registradas en el catálogo.
 * @returns {Array<{ key, label, description }>}
 */
export function allFeatures() {
  return FEATURE_GROUPS.flatMap((g) => g.features);
}

/**
 * @description Lookup rápido por clave.
 * @param {string} key
 * @returns {Object|null}
 */
export function getFeatureMeta(key) {
  return allFeatures().find((f) => f.key === key) || null;
}
